import { useState, useEffect, memo, useRef, useCallback } from "react";
import { IoMdRefresh, IoMdPlay, IoMdPause } from "react-icons/io";
import { useTranslation } from "react-i18next";

interface CountdownTimerProps {
  initialTime: number;
  onTimeUp?: () => void;
  isStopped?: boolean; // External control to stop the timer
  /** When provided, use this instead of initialTime to restore timer state (e.g. when switching back from answer view). Ignored in elapsedMode. */
  initialRemainingOverride?: number | null;
  /** Called when remaining time changes so parent can persist it across view switches */
  onTimeUpdate?: (remaining: number) => void;
  /** When true, timer is display-only (no play/pause on click) */
  disablePause?: boolean;
  /** When provided, show a round reset icon that calls this when clicked (e.g. start 20s for other team) */
  onResetClick?: () => void;
  /** When false, timer starts in paused state and begins only when user clicks */
  autoStart?: boolean;
  /** When true, count up from 0 (0,1,2,3...) to infinite; ignore initialTime. Turns red after elapsedRedThreshold seconds. */
  elapsedMode?: boolean;
  /** Seconds after which the display turns red in elapsed mode (default 55) */
  elapsedRedThreshold?: number;
  /** In elapsed mode, stop counting at this value (e.g. 60 for qr_code questions). */
  elapsedCap?: number;
  /** When provided, call this when the timer is started */
  handleStartSecondChance?: () => void;
  /** When countdown is stopped at 00:00, play resumes the player turn instead of toggling pause */
  onExpiredPlay?: () => void;
  hideIcons?: boolean;
}

const CountdownTimer = memo(({
  initialTime,
  onTimeUp,
  isStopped = false,
  initialRemainingOverride,
  onTimeUpdate,
  disablePause = false,
  onResetClick,
  autoStart = true,
  elapsedMode = false,
  elapsedRedThreshold = 55,
  elapsedCap,
  handleStartSecondChance,
  onExpiredPlay,
  hideIcons = false,
}: CountdownTimerProps) => {
  const { t } = useTranslation();
  const [timeRemaining, setTimeRemaining] = useState<number>(() =>
    // In elapsed mode, start from initialTime (so callers can pass the current elapsed seconds)
    elapsedMode
      ? initialTime
      : (initialRemainingOverride != null && initialRemainingOverride >= 0
        ? initialRemainingOverride
        : initialTime)
  );
  const [isPaused, setIsPaused] = useState(!autoStart);
  const hasCalledTimeUp = useRef(false);
  const onTimeUpdateRef = useRef(onTimeUpdate);
  const onTimeUpRef = useRef(onTimeUp);
  const lastReportedTimeRef = useRef<number | null>(null);
  const [hasStarted, setHasStarted] = useState(false);

  const isElapsedCapped =
    elapsedMode && elapsedCap != null && timeRemaining >= elapsedCap;
  const timerFrozen = isStopped || isPaused || isElapsedCapped;

  useEffect(() => {
    onTimeUpdateRef.current = onTimeUpdate;
  }, [onTimeUpdate]);

  useEffect(() => {
    onTimeUpRef.current = onTimeUp;
  }, [onTimeUp]);
  // Show "Ready" only for the initial qr_code timer (where initialTime is 0).
  // For other timers (e.g. second-chance 20s) we should show the numeric time instead.
  const isPreStartReadyState = !autoStart && isPaused && !hasStarted && initialTime === 0;


  const handleTimeUp = useCallback(() => {
    if (!hasCalledTimeUp.current && onTimeUpRef.current) {
      hasCalledTimeUp.current = true;
      onTimeUpRef.current();
    }
  }, []);

  // Format seconds to mm:ss
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Notify parent when displayed time changes (ref avoids parent re-renders on callback identity)
  useEffect(() => {
    if (lastReportedTimeRef.current === timeRemaining) return;
    lastReportedTimeRef.current = timeRemaining;
    onTimeUpdateRef.current?.(timeRemaining);
  }, [timeRemaining]);

  // Reset/sync: in elapsed mode only sync pause state; in countdown mode apply override/initialTime.
  useEffect(() => {
    if (!elapsedMode) {
      if (initialRemainingOverride != null && initialRemainingOverride >= 0) {
        setTimeRemaining(initialRemainingOverride);
      } else if (initialRemainingOverride === null) {
        setTimeRemaining(initialTime);
      }
      if (initialRemainingOverride != null || initialRemainingOverride === null) {
        hasCalledTimeUp.current = false;
      }
    }
    setIsPaused(!autoStart);
    // Reset "Ready" display when starting a new countdown in paused state.
    if (!autoStart) setHasStarted(false);
  }, [initialTime, initialRemainingOverride, autoStart, elapsedMode]);

  // When in elapsed mode, allow callers to change the starting elapsed seconds via initialTime.
  useEffect(() => {
    if (!elapsedMode) return;
    setTimeRemaining(initialTime);
  }, [initialTime, elapsedMode]);

  // Elapsed mode: count up every second until optional elapsedCap
  useEffect(() => {
    if (!elapsedMode || timerFrozen) return;
    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (elapsedCap != null && prev >= elapsedCap) return prev;
        return prev + 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [elapsedMode, timerFrozen, elapsedCap]);

  // Countdown logic (only when not elapsed mode)
  useEffect(() => {
    if (elapsedMode) return;
    if (timerFrozen) return;
    if (timeRemaining <= 0) {
      handleTimeUp();
      return;
    }
    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          handleTimeUp();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [elapsedMode, timerFrozen, timeRemaining, handleTimeUp]);

  const togglePause = () => {
    // "Ready" state: first click starts timer (even when disablePause is true).
    if (isPreStartReadyState) {
      if (!isStopped) {
        setHasStarted(true);
        setIsPaused(false);
      }
      return;
    }

    if (!disablePause && (elapsedMode || timeRemaining > 0)) {
      setIsPaused((prev) => !prev);
    }
  };

  const handleReset = () => {
    if (elapsedMode) {
      setTimeRemaining(0);
    }
    if (onResetClick) {
      onResetClick();
    }
  };

  // Critical: countdown = 10s or less; elapsed = at or past threshold (e.g. 55s)
  const isCritical = elapsedMode
    ? timeRemaining >= elapsedRedThreshold
    : timeRemaining <= 10 && timeRemaining >= 0;

  const timerContent = (
    <div className={`rounded-md h-full w-[150px] lg:w-[200px] 2xl:px-20 px-15 ${isCritical ? "bg-[#B91C1C]" : "bg-brown-primary-dark shadow-[0px_4px_0px_#87714A]"} 2xl:h-[3vw] xl:h-10 md:h-8 h-6`}>
      <div className="2xl:px-[1.45vw] 2xl:py-[1vw] xl:py-4 lg:py-4 md:py-3 py-1.5 sm:whitespace-nowrap text-white font-bold 2xl:text-[1.89vw] xl:text-xl/tight lg:text-base/tight text-sm/tight landscape:max-lg:text-sm/tight transition-all duration-200 text-center flex justify-center items-center 2xl:gap-[0.5vw] gap-3 landscape:max-lg:gap-0.5 w-full absolute inset-0 px-10">
        {!hideIcons && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (!elapsedMode && isStopped && timeRemaining === 0 && onExpiredPlay) {
                onExpiredPlay();
                return;
              }
              setIsPaused((prev) => !prev);
            }}
            className="flex items-center justify-center transition-colors cursor-pointer 2xl:w-[1.563vw] 2xl:min-w-[1.863vw] 2xl:h-[1.863vw] xl:min-w-5 min-w-5 xl:w-5 w-5 landscape:max-lg:w-5 xl:h-6 h-5 landscape:max-lg:h-5 rtl:-scale-x-100 shrink-0"
          >
            {isPaused ? <IoMdPlay className="w-full h-full text-white" /> : <IoMdPause className="w-full h-full text-white" />}
          </button>)}
        <span className="pt-1  px-2 text-3xl 2xl:text-4xl"
          onClick={!autoStart ? togglePause : undefined}
        >
          {isPreStartReadyState ? t("gameAnswer.ready") : formatTime(timeRemaining)}
        </span>
        {!hideIcons && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleReset();
              // handleStartSecondChance?.();
            }}
            className="flex items-center justify-center transition-colors cursor-pointer 2xl:w-[1.563vw] 2xl:min-w-[1.863vw] 2xl:h-[1.863vw] xl:min-w-5 min-w-5 xl:w-5 w-5 landscape:max-lg:w-5 xl:h-6 h-5 landscape:max-lg:h-5 rtl:-scale-x-100 shrink-0"
          >
            <IoMdRefresh className="w-full h-full text-white" />
          </button>
        )}
      </div>
      <style>{`
        @keyframes timer-blink {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.7;
          }
        }
        .animate-timer-blink {
          animation: timer-blink 1s  infinite;
        }
      `}</style>
    </div>
  );

  const className = "relative 2xl:h-[3.229vw] xl:h-8 md:h-8 h-8 landscape:max-lg:h-7.5 w-fit flex items-center justify-start ";

  if (disablePause) {
    // Display-only mode, but allow the initial "Ready" click to start the timer.
    if (isPreStartReadyState) {
      return (
        <button
          type="button"
          onClick={() => {
            if (!isStopped) setIsPaused(false);
          }}
          className={className + " cursor-pointer"}
          aria-label={t("gameAnswer.readyToStartTimer")}
          title={t("gameAnswer.ready")}
        >
          {timerContent}
        </button>
      );
    }

    // Display-only mode, no play/pause on click
    return <div className={className}>{timerContent}</div>;
  }

  return (
    <button
      type="button"
      // onClick={togglePause}
      className={className + " cursor-pointer"}
    // aria-label={isPreStartReadyState || isPaused || isStopped ? "Play timer" : "Pause timer"}
    // title={isPreStartReadyState || isPaused || isStopped ? "Play timer" : "Pause timer"}
    >
      {timerContent}
    </button>
  );
});

CountdownTimer.displayName = "CountdownTimer";

export default CountdownTimer;
